import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';

import { ProvidersOutcome } from '../queries';
import { providerStatusPresentation } from '../providerStatus';
import { useAudience } from '../audience';

interface Props {
  providers?: ProvidersOutcome;
  /** Cuántas ofertas aportó cada proveedor, si se puede calcular. */
  countsByProvider?: Record<string, number>;
}

function Dot({ color }: { color: string }) {
  return (
    <Box
      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
    />
  );
}

/**
 * Quién contestó la búsqueda y quién no. Ningún competidor tiene esto porque
 * ninguno hace fan-out a varios proveedores: si transferz se cayó y solo
 * respondió ineedtours, el asesor tiene que saberlo ANTES de decirle al cliente
 * que no hay disponibilidad.
 *
 * Con todo OK queda como una línea gris; si alguno falló, se queda a la vista.
 */
export function ProviderStatusStrip({ providers, countsByProvider }: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();

  // Qué proveedor respondió es información de operación, no del pasajero.
  if (!isAgent || !providers) return null;

  const { failed, timedOut, skipped, truncated, degraded, responded, hasProblems, isEmpty } =
    providerStatusPresentation(providers);
  if (isEmpty) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        px: 1.5,
        py: 1,
        borderColor: hasProblems ? 'warning.main' : 'divider',
      }}
    >
      {responded.map((provider) => (
        <Box key={`responded:${provider}`} display="flex" alignItems="center" gap={0.75}>
          <Dot color="success.main" />
          <Typography variant="caption" color="text.secondary">
            {provider}
            {countsByProvider?.[provider] === 0 ? (
              <Box component="span" color="text.secondary" ml={0.5}>
                {t('travelServices.providerNoResults')}
              </Box>
            ) : typeof countsByProvider?.[provider] === 'number' ? (
              <Box component="span" color="text.primary" fontWeight={600} ml={0.5}>
                {countsByProvider[provider]}
              </Box>
            ) : null}
          </Typography>
        </Box>
      ))}

      {failed.map((failure) => (
        <Box key={`failed:${failure.provider}`} display="flex" alignItems="flex-start" gap={0.75}>
          <Box pt={0.5}>
            <Dot color="error.main" />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="error.main">
              {t('travelServices.providerFailed', { provider: failure.provider })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {failure.reason}
            </Typography>
          </Box>
        </Box>
      ))}

      {timedOut.map((provider) => (
        <Box key={`timeout:${provider}`} display="flex" alignItems="center" gap={0.75}>
          <Dot color="warning.main" />
          <Typography variant="caption" color="warning.main">
            {t('travelServices.providerTimedOut', { provider })}
          </Typography>
        </Box>
      ))}

      {skipped.map((entry) => (
        <Box
          key={`skipped:${entry.connector}:${entry.reason}`}
          display="flex"
          alignItems="flex-start"
          gap={0.75}
        >
          <Box pt={0.5}>
            <Dot color="warning.main" />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="warning.main">
              {t('travelServices.providerSkipped', { provider: entry.provider })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`travelServices.providerSkippedReasons.${entry.reason}`)}
            </Typography>
          </Box>
        </Box>
      ))}

      {truncated.map((entry) => (
        <Box key={`truncated:${entry.provider}`} display="flex" alignItems="center" gap={0.75}>
          <Dot color="info.main" />
          <Typography variant="caption" color="info.main">
            {t('travelServices.providerTruncated', {
              provider: entry.provider,
              returned: entry.returned,
              total: entry.totalLabel,
            })}
          </Typography>
        </Box>
      ))}

      {degraded.map((entry) => (
        <Box key={`degraded:${entry.provider}`} display="flex" alignItems="center" gap={0.75}>
          <Dot color="warning.main" />
          <Typography variant="caption" color="warning.main">
            {t('travelServices.providerDegraded', { provider: entry.provider })}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}
